/* eslint-disable no-console */
/*
 * Photo / Video Gallery (server-rendered).
 *
 * Replaces the retired React gallery (client.js + components/media-gallery/**).
 * The GalleryPageController now renders every media item and filter group into
 * the markup; this module only wires up the interactivity that used to live in
 * Redux + React:
 *   - Images/Videos tab switching
 *   - client-side filtering through the shared filter bar (AND across groups,
 *     OR within a group); the bar owns the dropdowns, chips, Clear All, the
 *     mobile panel and URL sync
 *   - the photo lightbox for images, with its caption (product link, description,
 *     share links + add/remove from Favorites)
 *   - a simple video modal
 *   - lazyload + hash deep-linking + screen-reader result count
 *
 * The photo lightbox itself lives in modules/photo-lightbox.js, shared with the
 * My Account favorites grid.
 */
import Lazyload from './modules/lazyload';
import applyTilePattern, { isDesktop, measureCell } from './modules/gallery-tile-pattern';
import createPhotoLightbox from './modules/photo-lightbox';
import { createSharedFilterBar } from './modules/shared-filter-bar';

const galleryRoot = document.getElementById('media-gallery');

if (galleryRoot) {
  const FILTER_PREFIX = 'gallery';
  const COLLECTION_KEY = 'collection';
  const FILTER_GROUP_KEYS = [COLLECTION_KEY, 'solution', 'color', 'room', 'product'];
  const LAZY_OPTIONS = { rootMargin: '500px', placeholderClass: 'placeholder' };

  const list = document.getElementById('media-gallery-list');
  const filterBarRoot = document.querySelector(`[data-filter-bar="${FILTER_PREFIX}"]`);
  const tileRow = galleryRoot.querySelector('[data-gallery-collections]');
  const noMatchesMessage = galleryRoot.querySelector('.no-matches-message');
  const resultTotalLabel = galleryRoot.querySelector('.result-total-label');
  const items = Array.from(list.querySelectorAll('.gallery-item'));
  const imageItems = items.filter(item => item.classList.contains('gallery-item-image'));
  const mediaTabs = Array.from(galleryRoot.querySelectorAll('.gallery-media-tab'));

  let filterBar = null;
  let activeMediaType = mediaTabs.length ? mediaTabs[0].getAttribute('data-media-type') : 'Image';

  const tokensFor = (item, groupKey) => (
    (item.getAttribute(`data-${groupKey}`) || '').split(',').filter(token => token.length > 0)
  );

  const matchesGroups = (item, filters, exceptGroup) => (
    FILTER_GROUP_KEYS.every((groupKey) => {
      const selected = (filters && filters[groupKey]) || [];
      if (groupKey === exceptGroup || selected.length === 0) {
        return true;
      }
      const itemTokens = tokensFor(item, groupKey);
      return selected.some(value => itemTokens.indexOf(value) !== -1);
    })
  );

  const isExcludedCollectionOnly = (item, filters) => (
    item.getAttribute('data-collection-only') === 'true'
    && ((filters && filters[COLLECTION_KEY]) || []).length === 0
  );

  const itemMatches = (item, filters) => {
    if (item.getAttribute('data-media-type') !== activeMediaType) {
      return false;
    }
    // Videos have no filter metadata, so they ignore the filters.
    if (activeMediaType === 'Video') {
      return true;
    }
    if (isExcludedCollectionOnly(item, filters)) {
      return false;
    }
    return matchesGroups(item, filters, null);
  };

  // Queried live, not filtered from the init-time `items` snapshot: the tile pattern reorders
  // the dom and the lightbox indexes into this list, so a stale order would mismatch.
  const getVisibleImages = () => Array.from(
    list.querySelectorAll('.gallery-item-image:not(.hidden)'),
  );

  const syncCollectionTiles = (filters) => {
    if (!tileRow) {
      return;
    }
    const selected = (filters && filters[COLLECTION_KEY]) || [];
    Array.from(tileRow.querySelectorAll('[data-collection-tile]')).forEach((tile) => {
      const active = selected.indexOf(tile.getAttribute('data-collection-tile')) !== -1;
      tile.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const resultsGrid = galleryRoot.querySelector('.filtered-content-grid');

  // minified-header.js legitimately publishes 0px in its non-fixed layout, so the test has to
  // be for a finite number rather than for truthiness.
  const getHeaderHeight = () => {
    const height = parseFloat(document.documentElement.style.getPropertyValue('--header-height'));
    return Number.isFinite(height) ? height : 60;
  };

  const holdPinnedFilterBar = () => {
    if (!resultsGrid || !filterBarRoot || !filterBarRoot.classList.contains('is-stuck')) {
      if (resultsGrid) {
        resultsGrid.style.minHeight = '';
      }
      return;
    }
    const headerHeight = getHeaderHeight();
    const pinnedOffset = () => headerHeight + filterBarRoot.offsetHeight;
    resultsGrid.style.minHeight = `${window.innerHeight - pinnedOffset()}px`;

    window.requestAnimationFrame(() => {
      const offset = pinnedOffset();
      resultsGrid.style.minHeight = `${window.innerHeight - offset}px`;
      const gridTop = resultsGrid.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: gridTop - offset, behavior: 'instant' });
    });
  };

  const applyFilters = (filters) => {
    let visibleCount = 0;
    items.forEach((item) => {
      if (itemMatches(item, filters)) {
        item.classList.remove('hidden');
        visibleCount += 1;
      } else {
        item.classList.add('hidden');
      }
    });

    if (noMatchesMessage) {
      noMatchesMessage.classList.toggle('hidden', visibleCount !== 0);
    }
    if (resultTotalLabel) {
      resultTotalLabel.textContent = `Gallery updated, ${visibleCount} items shown`;
    }

    // Before holdPinnedFilterBar, which measures geometry and scrolls, and before
    // lazyload and the lightbox, which both depend on the final order.
    applyTilePattern(list, getVisibleImages());

    syncCollectionTiles(filters);
    holdPinnedFilterBar();

    Lazyload(LAZY_OPTIONS);
  };

  // ----- Tabs (Images / Videos) -----
  const setActiveTab = (mediaType) => {
    activeMediaType = mediaType;
    const showingVideos = mediaType === 'Video';

    // Video tiles need content-sized rows instead of the mosaic's square cells.
    list.classList.toggle('media-gallery-list--videos', showingVideos);

    mediaTabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-media-type') === mediaType;
      tab.parentElement.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    // Videos have no filter metadata, so hide the filter bar (matches React).
    if (filterBarRoot) {
      filterBarRoot.classList.toggle('d-none', showingVideos);
    }
    if (tileRow) {
      tileRow.classList.toggle('d-none', showingVideos);
    }

    if (filterBar) {
      filterBar.clear();
    } else {
      applyFilters(null);
    }
  };

  mediaTabs.forEach((tab) => {
    tab.addEventListener('click', () => setActiveTab(tab.getAttribute('data-media-type')));
  });

  // ----- Collection tiles (single-select) -----
  // One delegated listener so Glide's cloned and rebuilt slides work too. Glide only
  // suppresses clicks on links after a swipe, so a drag is filtered out here.
  if (tileRow) {
    const SWIPE_THRESHOLD = 10;
    let pointerStartX = null;

    tileRow.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
    });

    tileRow.addEventListener('click', (event) => {
      const startX = pointerStartX;
      pointerStartX = null;

      const tile = event.target.closest('[data-collection-tile]');
      if (!tile || !filterBar) {
        return;
      }
      if (startX !== null && Math.abs(event.clientX - startX) > SWIPE_THRESHOLD) {
        return;
      }

      const slug = tile.getAttribute('data-collection-tile');
      const state = filterBar.getState();
      const active = state[COLLECTION_KEY].indexOf(slug) !== -1;
      filterBar.setState(Object.assign({}, state, { [COLLECTION_KEY]: active ? [] : [slug] }));
    });
  }

  // ===== Photo lightbox =====
  // The favorite button is server-rendered per image into a hidden .js-lightbox-actions slot
  // (_GalleryFavoriteButton.cshtml) which the module clones into the modal.
  createPhotoLightbox({
    modalId: 'photo-lightbox',
    container: list,
    itemSelector: '.gallery-item-image',
    getItems: getVisibleImages,
    updateHash: true,
    requireAuthToAdd: true,
  });

  // ===== Video modal =====
  const closeVideoModal = () => {
    const modal = document.querySelector('.gallery-video-modal-container');
    if (modal) {
      modal.parentNode.removeChild(modal);
    }
  };

  const openVideoModal = (videoItem) => {
    const inner = videoItem.querySelector('[data-video-iframe]');
    const iframeSrc = inner.getAttribute('data-video-iframe');
    const title = inner.getAttribute('data-video-title') || '';
    const description = inner.getAttribute('data-video-description') || '';
    const container = document.createElement('div');
    container.className = 'gallery-video-modal-container photo-video-gallery';
    container.innerHTML = `
      <div class="modal gallery-modal modal-visible" tabindex="0">
        <div class="modal-overlay"></div>
        <div class="modal-panel">
          <button type="button" class="close-btn" aria-label="Close Modal"></button>
          <div class="modal-inner">
            <div class="container modal-content">
              <div class="row row-eq-height">
                <div class="col-xs-12 col-sm-12 col-md-8 col-lg-9">
                  <div class="modal-video">
                    <iframe class="modal-iframe" src="${iframeSrc}/?rel=0" title="${title}" allowfullscreen></iframe>
                  </div>
                </div>
                <div class="col-xs-12 col-sm-12 col-md-4 col-lg-3">
                  <div class="modal-description">
                    <h2 class="modal-title">${title}</h2>
                    <div class="video-description"><p>${description}</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>`;
    document.body.appendChild(container);
    container.addEventListener('click', (event) => {
      if (
        event.target.classList.contains('modal-overlay')
        || event.target.classList.contains('close-btn')
        || event.target.classList.contains('modal-visible')
      ) {
        closeVideoModal();
      }
    });
  };

  list.addEventListener('click', (event) => {
    const videoItem = event.target.closest('.gallery-item-video');
    if (videoItem) {
      event.preventDefault();
      openVideoModal(videoItem);
    }
  });

  // ===== Deep-linking (open a specific photo on load) =====

  // ?photo= is the shareable form, written by the lightbox's share button. The hash is the
  // older form, still written as the user arrows between photos, and still read here so that
  // links copied out of the address bar before ?photo= existed keep resolving.
  const readDeepLinkId = () => {
    const params = new URLSearchParams(window.location.search);
    const photoId = (params.get('photo') || '').trim();

    if (!photoId) {
      const hashId = window.location.hash.substr(1);
      return hashId === 'media-gallery-list' ? '' : hashId;
    }

    params.delete('photo');
    const query = params.toString();
    const search = query ? `?${query}` : '';
    window.history.replaceState(null, '', `${window.location.pathname}${search}`);

    return photoId;
  };

  const openDeepLinkItem = () => {
    const photoId = readDeepLinkId();
    if (!photoId) {
      return;
    }
    // Exact match on the Widen id rather than a data-src substring: the id is now rendered on
    // its own attribute, and a substring test would match any url that merely contained it.
    const target = imageItems.find(item => item.getAttribute('data-widen-id') === photoId);
    if (!target) {
      return;
    }
    if (target.classList.contains('hidden') && filterBar) {
      const collections = target.getAttribute('data-collection-only') === 'true'
        ? tokensFor(target, COLLECTION_KEY)
        : [];
      if (collections.length) {
        filterBar.setState({ [COLLECTION_KEY]: [collections[0]] });
      } else {
        filterBar.clear();
      }
    }
    const btn = target.querySelector('button');
    if (btn) {
      btn.click();
    }
  };

  if (filterBarRoot) {
    const stuckHysteresis = 4;
    let stuckQueued = false;

    const syncFilterBarStuck = () => {
      stuckQueued = false;
      if (filterBarRoot.classList.contains('d-none')) {
        filterBarRoot.classList.remove('is-stuck');
        return;
      }
      const isStuck = filterBarRoot.classList.contains('is-stuck');
      const limit = getHeaderHeight() + (isStuck ? stuckHysteresis : 1);
      const { top } = filterBarRoot.getBoundingClientRect();
      filterBarRoot.classList.toggle('is-stuck', top <= limit);
    };

    const queueFilterBarStuck = () => {
      if (stuckQueued) {
        return;
      }
      stuckQueued = true;
      window.requestAnimationFrame(syncFilterBarStuck);
    };

    window.addEventListener('scroll', queueFilterBarStuck, { passive: true });
    window.addEventListener('resize', queueFilterBarStuck);
    syncFilterBarStuck();
  }

  // ----- Tile pattern on resize -----
  // A width change needs a re-measure, but the span table only swaps at md, so a full
  // re-pack is reserved for crossing that boundary.
  let wasDesktop = isDesktop();
  let lastWidth = list.clientWidth;
  let tilePatternTimer = null;

  window.addEventListener('resize', () => {
    if (tilePatternTimer) {
      window.clearTimeout(tilePatternTimer);
    }
    tilePatternTimer = window.setTimeout(() => {
      const width = list.clientWidth;
      if (width === lastWidth) {
        return;
      }
      lastWidth = width;

      const nowDesktop = isDesktop();
      if (nowDesktop === wasDesktop) {
        measureCell(list);
        return;
      }
      wasDesktop = nowDesktop;
      applyTilePattern(list, getVisibleImages());
      Lazyload(LAZY_OPTIONS);
    }, 150);
  });

  // ----- init -----
  filterBar = createSharedFilterBar({
    idPrefix: FILTER_PREFIX,
    groupKeys: FILTER_GROUP_KEYS,
    urlSync: true,
    onFiltersChange: applyFilters,
  });
  if (!filterBar) {
    applyFilters(null);
  }
  openDeepLinkItem();
}
/* eslint-enable no-console */
